import { useState, useEffect } from "react";

import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, size } from "lodash";

import { TextField } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from "@mui/material/Checkbox";

import Snackbar from "@plannel/components/Snackbar";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";

import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import Dialog from "../Dialog";
import { FS } from "@plannel/utils/font-util";

const { RP } = CodeUtil.module;
const { snackBarMessageColor } = ColorUtil;
const DIALOG_OBJ = {OK: "OK", CANCEL: "CANCEL", INFO: "info", RUN: "run"};
const ScenarioReleaseModal = props => {
  const { t, close, open, reload, moduleCd, setIsReleasing, service, selectScenario, releaseVersion, dialogContent = {}, nameProperty = "name", updateOrderMethod: initialUpdateOrderMethod = false } = props;

  const moduleNm = moduleCd.toLowerCase();
  const [scenarioNm, setScenarioNm] = useState("");
  const initReleaseNm = t(moduleNm) + dateUtils.formatDate(new ZDate(), "yyyyMMdd");
  const [relVersionNm, setRelVersionNm] = useState(initReleaseNm);
  const [desc, setDesc] = useState("");

  const [updateOrderMethod, setUpdateOrderMethod] = useState(initialUpdateOrderMethod);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const handleClose = () => {
    close(false);
  };
  
  const handleRelease = async () => {
    const params = {
      ...selectScenario,
      releaseNm: relVersionNm,
      releaseDesc: desc,
      ...(isEqual(moduleCd, RP) ? { updateOrderMethod } : {})
    };

    try {
      setIsReleasing?.(true);
      handleClose();

      const response = await service.release(params);

      releaseVersion?.(response.data);
      setSnackInfo({
        open: true,
        severity: snackBarMessageColor.saveSuccess,
        content: t("MSG_SaveSuccess")
      });

      await Promise.resolve(reload?.(true));
    } catch (e) {
      const errorMsg = e.response?.data?.message;
      setSnackInfo({
        open: true,
        severity: snackBarMessageColor.saveFail,
        content: errorMsg
          ? t(errorMsg, { name: `[${relVersionNm}]`, target: `[${scenarioNm}]` })
          : t("MSG_SaveFailed")
      });
      debug(e);
    } finally {
      setIsReleasing?.(false);
      handleClose();
    }
  };

  const handleDialog = (action, _1, _2) => {
    if (action === DIALOG_OBJ.OK) {
      handleRelease();
    }
  }

  const handleReleaseModal = () => {
    if(isEmpty(dialogContent)) {
      handleRelease();
    } else {
      const title = dialogContent?.title ? dialogContent.title : DIALOG_OBJ.INFO;
      const content = dialogContent?.content ? dialogContent.content : t(DIALOG_OBJ.RUN);
      setDialogInfo({ open: true, title, content, actionName: DIALOG_OBJ.OK });
    }
  }
  useEffect(() => {
    if (open) {
      if (size(selectScenario) > 0) {
        setScenarioNm(selectScenario?.[nameProperty]);
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoScenario") });
      }
      setUpdateOrderMethod(initialUpdateOrderMethod);
    }

    return () => {
      setScenarioNm("");
      setRelVersionNm(initReleaseNm);
      setDesc("");
      setUpdateOrderMethod(initialUpdateOrderMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUpdateOrderMethod]);

  return (
    <>
      {/*
        * size lg: 제목이 t(moduleNm) + t("releaseScenario") 라 길다 —
        * vi "Quy Hoạch Tổng Thể Phiên Bản Phát Hành"(38자)는 20px/700 에서 약 400px + 패딩 40 이라 md(360) 를 넘긴다.
        */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={`${t(moduleNm)} ${t("releaseScenario")}`}
        onSave={handleReleaseModal}
        saveLabel={t("release")}
        size="lg">
        <TextField
          label={t("scenarioName")}
          value={scenarioNm}
          fullWidth
          size="small"
          disabled
          error={size(selectScenario) < 1}
        />
        <TextField
          label={t("releaseVersion")}
          value={relVersionNm}
          fullWidth
          size="small"
          type="string"
          sx={{ mt: 2 }}
          onChange={event => setRelVersionNm(event.target.value)}
        />
        <TextField
          label={t("descTxt")}
          value={desc}
          fullWidth
          size="small"
          type="string"
          multiline
          rows={2}
          sx={{ mt: 2 }}
          onChange={e => setDesc(e.target.value)}
        />
        {
          isEqual(moduleCd, RP) ? (
            <FormControlLabel
              control={<Checkbox name="activeFlg" sx={{ pl: "5px" }} checked={updateOrderMethod} onChange={() => setUpdateOrderMethod(!updateOrderMethod)} />}
              label={t("overrideOrderMethod")}
              sx={{ mt: 1, mx: 0 }}
              componentsProps={{ typography: { sx: { fontSize: FS.TITLE }, width: "100%" } }}
            />
          ) : ""
        }
      </BaseConfigModal>
      {!isEmpty(dialogContent) &&
        <Dialog
          open={dialogInfo.open}
          onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
          actionName={dialogInfo.actionName}
          title={dialogInfo.title}
          content={dialogInfo.content}
          onHandler={handleDialog}
        />
      }
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ScenarioReleaseModal);
